/**
 * Conversation Flow: greeting lives in a dedicated "greeting" node; this module
 * syncs workflow JSON with the script document's greetingTemplate field.
 */

import { v4 as uuidv4 } from "uuid";

/**
 * @param {object} workflow — { nodes, edges, agent? }
 * @returns {string} instruction text from the first greeting node reachable from Start (else first greeting in node order)
 */
export function extractGreetingTemplateFromWorkflow(workflow) {
  if (!workflow?.nodes?.length) return "";
  const greetingNodes = workflow.nodes.filter((n) => n.type === "greeting");
  if (greetingNodes.length === 0) return "";

  const edges = workflow.edges || [];
  const bySource = new Map();
  edges.forEach((e) => {
    if (!bySource.has(e.source)) bySource.set(e.source, []);
    bySource.get(e.source).push(e);
  });

  const startIds = new Set(workflow.nodes.filter((n) => n.type === "start").map((n) => n.id));
  const seen = new Set();
  const queue = [...startIds];
  while (queue.length) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    const node = workflow.nodes.find((n) => n.id === id);
    if (node?.type === "greeting") {
      return String(node.data?.instruction || "").trim();
    }
    (bySource.get(id) || []).forEach((e) => queue.push(e.target));
  }

  const first = greetingNodes[0];
  return String(first?.data?.instruction || "").trim();
}

/**
 * If the document has greetingTemplate but no greeting node (legacy), insert one after Start.
 * @param {object} workflow
 * @param {string} [greetingTemplate] — from script doc
 * @returns {object} workflow (possibly with new greeting node + edges)
 */
export function migrateWorkflowWithGreetingFromDoc(workflow, greetingTemplate) {
  const g = String(greetingTemplate || "").trim();
  if (!g) return workflow;
  const nodes = workflow.nodes || [];
  if (nodes.some((n) => n.type === "greeting")) return workflow;

  const startNode = nodes.find((n) => n.type === "start");
  const greetingId = `greeting_${uuidv4()}`;
  const edges = [...(workflow.edges || [])];
  const startId = startNode?.id;
  const outgoing = startId ? edges.filter((e) => e.source === startId) : [];

  const transitions =
    outgoing.length > 0
      ? outgoing.map((_, i) => ({
          handleId: `transition-${uuidv4()}`,
          text: i === 0 ? "Next" : `Branch ${i + 1}`,
        }))
      : [{ handleId: `transition-${uuidv4()}`, text: "Next" }];

  const greetingNode = {
    id: greetingId,
    type: "greeting",
    data: {
      title: "Greeting",
      instruction: g,
      transition: transitions,
    },
    position: startNode ? { x: startNode.position.x + 200, y: startNode.position.y } : { x: 400, y: 333 },
  };

  const newNodes = [...nodes, greetingNode];
  if (!startId) {
    return { ...workflow, nodes: newNodes, edges, agent: workflow.agent || {} };
  }

  const restEdges = edges.filter((e) => e.source !== startId);
  const newEdges = [...restEdges, { id: `e_${startId}_${greetingId}`, source: startId, target: greetingId }];

  outgoing.forEach((oe, i) => {
    newEdges.push({
      id: `e_${greetingId}_${oe.target}_${i}`,
      source: greetingId,
      sourceHandle: transitions[i].handleId,
      target: oe.target,
    });
  });

  return { ...workflow, nodes: newNodes, edges: newEdges, agent: workflow.agent || {} };
}
